import React, { useCallback, useEffect, useRef, useState } from 'react';
import { App as CapApp } from '@capacitor/app';
import { useAuth } from '../context/AuthContext';
import { LogoIcon } from './Logo';
import { isNative, isBiometricLockEnabled, runBiometricAuth } from '../utils/biometric';

// Candado biométrico sobre el contenido de la app. Si el bloqueo está activado y
// hay sesión, exige Face ID / Touch ID / huella al arrancar en frío y al volver de
// segundo plano. Solo actúa en nativo.
//
// El diálogo biométrico de iOS manda la app a "inactiva" y de vuelta a "activa",
// lo que dispararía un relock en bucle. Por eso `unlockingRef` ignora los cambios
// de estado mientras la biometría está en curso, y solo se re-bloquea si hubo un
// paso real a segundo plano (`backgroundedRef`).
export const BiometricGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, logout } = useAuth();
  const active = isNative() && isAuthenticated && isBiometricLockEnabled();

  const [locked, setLocked] = useState(active);
  const [failed, setFailed] = useState(false);
  const unlockingRef = useRef(false);
  const backgroundedRef = useRef(false);

  const authenticate = useCallback(async () => {
    if (unlockingRef.current) return;
    unlockingRef.current = true;
    setFailed(false);
    const ok = await runBiometricAuth('Desbloquea MalDolor');
    setLocked(!ok);
    setFailed(!ok);
    unlockingRef.current = false;
    backgroundedRef.current = false;
  }, []);

  useEffect(() => {
    if (!active) {
      setLocked(false);
      return;
    }
    // Bloqueo al montar (arranque en frío)
    setLocked(true);
    authenticate();

    // addListener es asincrono: si el componente se desmonta antes de que resuelva,
    // la bandera cancelled asegura que el listener se quita igualmente y no se fuga.
    let handle: { remove: () => void } | undefined;
    let cancelled = false;
    CapApp.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        // Paso a segundo plano real (no el parpadeo del diálogo biométrico)
        if (!unlockingRef.current) backgroundedRef.current = true;
      } else if (backgroundedRef.current && !unlockingRef.current) {
        setLocked(true);
        authenticate();
      }
    }).then((h) => {
      if (cancelled) h.remove();
      else handle = h;
    });

    return () => {
      cancelled = true;
      handle?.remove();
    };
  }, [active, authenticate]);

  if (!active || !locked) return <>{children}</>;

  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[var(--color-bg)] p-8">
      <LogoIcon size={80} className="mb-6" />
      <p className="text-[var(--color-text)] font-medium text-lg mb-1">MalDolor está bloqueado</p>
      <p className="text-[var(--color-text-muted)] text-sm mb-8 text-center">
        Usa tu biometría para continuar
      </p>
      <button
        onClick={authenticate}
        className="w-full max-w-xs py-3.5 px-4 rounded-2xl bg-[var(--color-accent)] text-white font-medium"
      >
        {failed ? 'Reintentar' : 'Desbloquear'}
      </button>
      <button
        onClick={logout}
        className="mt-4 text-sm text-[var(--color-text-muted)] underline"
      >
        Cerrar sesión
      </button>
    </div>
  );
};
